import {
  type Amount,
  AmountFrequency,
  type GoalOverview,
  GoalOverviewPayoutFrequency,
  GoalOverviewSubType,
  GoalOverviewType,
  type GoalPayoutPoint,
} from "Infrastructure/ApiZfp/Schema";
import { type ResourcePathType, Resources } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { inflectYears } from "Infrastructure/Utils/LanguageUtils";
import { type Translate, formatMonthYear } from "./dates";

export type GoalCardFact = {
  label: string;
  value: string;
};

type FactSource = [label: ResourcePathType, value: string | null];

const Timeline = Resources.AnalysisDetail.Goals.Timeline;

const formatAmount = (amount: Amount | null, t: Translate) => {
  if (!amount) return null;

  const formatted = formatCurrency(amount.value, amount.currencyCode, 0);
  return amount.frequency === AmountFrequency.monthly ? t(Timeline.MonthlyAmount, { amount: formatted }) : formatted;
};

const formatPayoutPoint = (point: GoalPayoutPoint | null, t: Translate) => {
  if (!point) return null;

  const age = inflectYears(point.age, t);
  return point.date ? t(Timeline.PayoutPoint, { age, date: formatMonthYear(point.date) }) : age;
};

const getFactSources = (goal: GoalOverview, t: Translate): FactSource[] => {
  if (goal.type === GoalOverviewType.pension) {
    return [
      [Timeline.PayoutFrequency, t(Timeline.Regular)],
      [Timeline.MonthlyRent, formatAmount(goal.rentAmount, t)],
      [Timeline.PayoutFrom, formatPayoutPoint(goal.payoutStart, t)],
      [Timeline.PayoutTo, formatPayoutPoint(goal.payoutEnd, t)],
    ];
  }

  if (goal.type === GoalOverviewType.housing && goal.subType === GoalOverviewSubType.housing_property) {
    return [
      [Timeline.PayoutFrequency, t(Timeline.Regular)],
      [Timeline.TargetSavingsAmount, formatAmount(goal.remainingSavingsAmount, t)],
      [Timeline.LoanPaymentAmount, formatAmount(goal.loanPaymentAmount, t)],
    ];
  }

  if (goal.type === GoalOverviewType.other && goal.payoutFrequency === GoalOverviewPayoutFrequency.regular) {
    return [
      [Timeline.PayoutFrequency, t(Timeline.Regular)],
      [Timeline.TargetAmount, formatAmount(goal.targetAmount, t)],
      [Timeline.PayoutAmount, formatAmount(goal.payoutAmount, t)],
    ];
  }

  return [
    [Timeline.PayoutFrequency, t(Timeline.Single)],
    [Timeline.Amount, formatAmount(goal.amount, t)],
  ];
};

/** Card facts in a fixed order per goal type, payout first; missing ones are left out. */
export const getGoalCardFacts = (goal: GoalOverview, t: Translate): GoalCardFact[] =>
  getFactSources(goal, t).flatMap(([label, value]) => (value ? [{ label: t(label), value }] : []));
